import { api, useFetch } from "@/plugins/api";
import type { AccountInfo } from "@azure/msal-browser";
import { defineStore } from "pinia";
import * as Sentry from '@sentry/vue'
import type { PermissionGroup, User } from "@/infrastructure/types";
import type { UserQuery } from "@/infrastructure/queries";
import { Permission } from "@/infrastructure/enums";

export const useUserStore = defineStore('user', () => {
  const user = ref<User>()
  const azureAccount = ref<AccountInfo>()
  const roles = ref<string[]>([])
  const userImpersonation = ref(false)
  const startPageName = ref(localStorage.getItem('startPageName') ?? 'insights')
  const isAuthenticated = computed(() => !!azureAccount)
  const isSuperAdmin = computed(() => !userImpersonation.value && user.value?.isSuperAdmin)

  const permissionGroups: PermissionGroup[] = [
    {
      name: 'Contracts',
      permissions: [
        {
          description: 'View contracts',
          value: Permission.ContractsRead
        },
        {
          description: 'Manage linked shops, edit rules and approvals',
          value: Permission.ContractsWrite,
          dependencies: [
            Permission.ContractsRead
          ]
        }
      ]
    },
    {
      name: 'Finalize period',
      permissions: [
        {
          description: 'View reports',
          value: Permission.FinalizePeriodRead
        },
        {
          description: 'Approve, recalculate and create missing reports',
          value: Permission.FinalizePeriodWrite,
          dependencies: [
            Permission.FinalizePeriodRead
          ]
        },
        {
          description: 'View and publish invoice drafts',
          value: Permission.FinalizePeriodInvoiceWrite,
          dependencies: [
            Permission.FinalizePeriodRead,
            Permission.FinalizePeriodWrite,
          ]
        }
      ]
    },
    {
      name: 'Invoices',
      requiredFeature: 'invoicing',
      permissions: [
        {
          description: 'View invoice configuration',
          value: Permission.InvoiceConfigurationRead
        },
        {
          description: 'Edit invoice configuration',
          value: Permission.InvoiceConfigurationWrite,
          dependencies: [
            Permission.InvoiceConfigurationRead
          ]
        }
      ]
    },
    {
      name: 'Locations',
      permissions: [
        {
          description: 'View all locations',
          value: Permission.LocationsRead
        },
        {
          description: 'Add, edit and delete locations',
          value: Permission.LocationsWrite,
          dependencies: [
            Permission.LocationsRead
          ]
        }
      ]
    },
    {
      name: 'Manual reporting',
      permissions: [
        {
          description: 'View manual reports',
          value: Permission.ManualReportingRead
        },
        {
          description: 'Input manual reports',
          value: Permission.ManualReportingWrite,
          dependencies: [
            Permission.ManualReportingRead
          ]
        },
      ]
    },
    {
      name: 'Operators',
      permissions: [
        {
          description: 'View operators and units',
          value: Permission.OperatorsRead
        },
        {
          description: 'Add, edit and delete operators and units',
          value: Permission.OperatorsWrite,
          dependencies: [
            Permission.OperatorsRead
          ]
        }
      ]
    },
    {
      name: 'Product brands',
      permissions: [
        {
          description: 'View all product brands',
          value: Permission.BrandsRead
        },
        {
          description: 'Add, edit and delete product brands',
          value: Permission.BrandsWrite,
          dependencies: [
            Permission.BrandsRead
          ]
        }
      ]
    },
    {
      name: 'Users',
      permissions: [
        {
          description: 'View all users',
          value: Permission.UsersRead
        },
        {
          description: 'Invite and edit users and their permission set',
          value: Permission.UsersWrite,
          dependencies: [
            Permission.UsersRead
          ]
        },
        {
          description: 'Change users\' permission set',
          value: Permission.UserPermissionSetWrite,
          dependencies: [
            Permission.UsersRead,
            Permission.UsersWrite
          ]
        }
      ]
    },
  ]

  async function getUser() {
    try {
      const { data, status } = await api.get('users/me')

      if (status !== 200) {
        throw data
      }

      user.value = data
    } catch (error) {
      console.error(error)
    }
  }

  async function getUsers(query?: UserQuery): Promise<User[]> {
    const data = await useFetch('users', query)
    return data
  }

  function hasPermission(permission: Permission): boolean {
    if (user.value?.isSuperAdmin) return true
    if (!user.value?.permissionSet) return false
    return user.value.permissionSet.permissions.includes(permission)
  }

  watch(azureAccount, (value) => {
    if (!value) {
      Sentry.setUser(null)
    }

    else {
      // Set Sentry user
      Sentry.setUser({
        id: value.localAccountId,
        email: value.username,
        username: value.name
      })

      // Heap
      if (window.heap.identify) {
        window.heap.identify(value.name)
      }

      if (window.heap.addUserProperties) {
        window.heap.addUserProperties({
          'Name': value.name,
          'Email': value.username,
          'Azure ID': value.localAccountId
        })
      }
    }
  })

  watch(startPageName, (value) => {
    localStorage.setItem('startPageName', value)
  })

  return {
    user,
    azureAccount,
    isAuthenticated,
    roles,
    userImpersonation,
    startPageName,
    isSuperAdmin,
    permissionGroups,
    getUser,
    getUsers,
    hasPermission
  }
})